<template>
  <ai-room-details
    v-model="open"
    :room="
      // eslint-disable-next-line vue/no-deprecated-filter
      room?.accommodationRoom as Accommodation | undefined
    "
    :carousel-component="BookingRoomDetailsCarouselItem"
    @popin-close="onPopinClose" />
</template>

<script setup lang="ts">
import BookingRoomDetailsCarouselItem from './BookingRoomDetailsCarouselItem.vue';

import { pageView } from '~~/helpers/gtm.helper';
import type { AvailableRoom, Accommodation } from '~~/domains/graphql';

type Props = {
  modelValue: boolean;
  room?: AvailableRoom;
};

type Emits = {
  (event: 'update:model-value', value: boolean): void;
  (event: 'popin-close'): void;
};

const props = defineProps<Props>();
const emits = defineEmits<Emits>();

pageView({
  page_category: 'hotel room full details',
  page_name: 'hotel::roompage',
  page_sub_category_level_1: 'hotel',
  page_sub_category_level_2: 'roompage',
});

const open = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emits('update:model-value', value);
  },
});

const onPopinClose = () => {
  emits('popin-close');
};
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/mq';

.BookingRoomsDetails {
  padding: constants.$padding-03;
  max-width: 100%;
}

.BookingRoomsDetails-carousel {
  margin-bottom: constants.$margin-02;

  @media (mq.$from-medium) {
    padding-right: constants.$padding-03;
  }
}

.BookingRoomsDetails-carousel-actions {
  margin-bottom: constants.$margin-03;

  @media (mq.$from-medium) {
    margin-bottom: constants.$margin-04;
  }
}

.BookingRoomsDetails-carouselAction {
  margin-right: constants.$margin-03;
}

.BookingRoomDetails-title {
  margin-bottom: constants.$margin-01;
}

.BookingRoomDetails-description {
  margin-bottom: constants.$margin-02;

  @media (mq.$from-medium) {
    margin-bottom: constants.$margin-03;
  }
}

.BookingRoomDetails-topAmenities {
  display: flex;
  flex-direction: column;
  margin-bottom: constants.$margin-03;

  @media (mq.$from-medium) {
    flex-direction: row;
    flex-wrap: wrap;
    margin-bottom: constants.$margin-04;
  }
}

.BookingRoomDetails-topAmenity {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: constants.$inner-02;

  @media (mq.$from-medium) {
    margin-bottom: 0;

    &:not(:last-of-type)::after {
      content: '';
      height: 2px;
      width: 2px;
      border-radius: 100%;
      background-color: colors.$stratos-600;
      margin-left: constants.$inner-04;
      margin-right: constants.$inner-04;
    }
  }
}

.BookingRoomDetails-topAmenity-icon {
  margin-right: constants.$inner-02;
}

.BookingRoomDetails-otherAmenities {
  background-color: colors.$gold-100;
  padding: constants.$padding-03;
}

.BookingRoomDetails-otherAmenities-wrapper {
  margin-top: constants.$margin-03;
  display: flex;
  flex-direction: column;

  @media (mq.$from-medium) {
    flex-direction: row;
    flex-wrap: wrap;
    column-gap: constants.$inner-02;
  }
}

.BookingRoomDetails-otherAmenity {
  display: inline-block;
  flex-basis: 100%;
  margin-bottom: constants.$margin-03;

  @media (mq.$from-medium) {
    flex-basis: calc(50% - (constants.$inner-02 / 2));
    margin-bottom: 0;
  }
}

.BookingRoomDetails-otherAmenity-label {
  margin-bottom: constants.$inner-03;
}

.BookingRoomDetails-otherAmenity-facilityLabel {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: constants.$inner-02;

  &::before {
    content: '';
    display: inline-block;
    height: 4px;
    width: 4px;
    border-radius: 100%;
    background-color: colors.$stratos-900;
    margin-left: constants.$inner-02;
    margin-right: constants.$inner-02;
  }
}

.BookingRoomDetails-otherAmenity-finalLabel {
  display: flex;
  flex-direction: row;
  align-items: baseline;
  margin-bottom: constants.$inner-02;
}

.BookingRoomDetails-otherAmenity-finalIcon {
  margin-right: constants.$inner-02;
}
</style>
